import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import api from '../../../services/api'
import { useSelector, useDispatch } from 'react-redux'

import { isFunction } from '../../../utils/customMethods'
import { setUser } from '../../../redux/user/actions'

import {
  Divider,
  CircularProgress,
  Grid,
  InputAdornment,
  OutlinedInput,
  InputLabel,
  FormControl,
  FormHelperText,
} from '@material-ui/core'

import { useTheme } from '@material-ui/core/styles'

import { CloseRounded as CloseIcon } from '@material-ui/icons'

import IconButton from '../../atoms/inputs/iconButton'
import Typography from '../../atoms/display/typography'
import Avatar from '../../atoms/display/avatar'
import Button from '../../atoms/inputs/button'
import Snackbar from '../../atoms/feedback/snackbar'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import useStyles from './userSettingsStyle'

const userSettings = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const theme = useTheme()
  const classes = useStyles()
  const [isVisiblePassword, setIsVisiblePassword] = useState(false)
  const [isVisibleRepeatPassword, setIsVisibleRepeatPassword] = useState(false)
  const [formError, setFormError] = React.useState({})
  const [snackbar, setSnackbar] = React.useState(false)
  const [pictureLoading, setPictureLoading] = React.useState(false)
  const [updatePictureLoading, setUpdatePictureLoading] = React.useState(false)
  const [updatePasswordLoading, setUpdatePasswordLoading] =
    React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState('')
  const [base64, setBase64] = React.useState('')
  const user = useSelector((state) => state.user)
  const { enqueueSnackbar } = useSnackbar()

  const newPassRef = useRef()
  const newPassRepeatRef = useRef()

  const setSelectedFile = (event) => {
    var reader = new FileReader()
    if (event.target.files[0]) {
      reader.readAsDataURL(event.target.files[0])
      reader.onloadstart = () => {
        setPictureLoading(true)
      }
      reader.onloadend = async () => {
        setPictureLoading(false)
        setBase64(reader.result)
      }
    }
  }

  const updatePicture = async () => {
    setUpdatePictureLoading(true)
    api({ method: 'PATCH', url: '/user/profile', data: { avatar: base64 } })
      .then((response) => {
        setUpdatePictureLoading(false)
        dispatch(setUser({ ...user, url_avatar: base64 }))
        setBase64('')
        enqueueSnackbar(t('toasts.pictureUpdated'), { variant: 'success' })
      })
      .catch((error) => {
        setUpdatePictureLoading(false)
        let message = error.response?.data?.message?.[0]?.value
        let parseMessage = t(`commons.errors.${message}`)
        if (parseMessage.startsWith('commons.errors')) {
          enqueueSnackbar(t('commons.errors.theFileCouldNotBeSent'), {
            variant: 'error',
          })
        } else {
          enqueueSnackbar(parseMessage, { variant: 'error' })
        }
      })
  }

  const passwordCheck = async () => {
    const password = newPassRef?.current?.value
    const passwordRepeat = newPassRepeatRef?.current?.value
    let error = false

    // Reset object
    setFormError({})

    if (password?.length > 0 && passwordRepeat?.length > 0) {
      if (password !== passwordRepeat) {
        error = true
        setFormError((formError) => ({
          ...formError,
          passwordRepeat: 'passwordsAreNotTheSame',
        }))
      }

      if (
        !password.match(
          /^(?=.*[A-Z])(?=.*[0-9])(?=.*[~`!@#$%^&*()--+={}[\]|\\:;"'<>,.?/_]).{8,}$/
        )
      ) {
        error = true
        setFormError((formError) => ({
          ...formError,
          newPassword:
            'Password must be at least 8 characters long, contain at least one uppercase character, contain at least one number and contain at least one special character',
        }))
      }
      if (!error) {
        !updatePasswordLoading && submit()
      }
    } else {
      let errors = {}

      if (password?.length === 0) {
        errors.newPassword = 'password is required'
      }
      if (passwordRepeat?.length === 0) {
        errors.passwordRepeat = 'password is required'
      }

      setFormError(errors)
    }
  }

  const submit = async () => {
    setUpdatePasswordLoading(true)

    try {
      const response = await api.post(`/user/password-change`, {
        password: newPassRef?.current?.value,
        slug: process.env.REACT_APP_SLUG,
      })

      if (response.status === 200) {
        enqueueSnackbar(t('toasts.passwordUpdated'), { variant: 'success' })
        isFunction(props?.close) && props.close()
      } else {
        throw response.data
      }
    } catch (error) {
      const data = error.response?.data
      const apiError = data?.message?.find((el) => el.value === 'password must be different from the previous')

      apiError &&
        setFormError(() => ({
          newPassword: 'password must be different from the previous',
        }))
    } finally {
      setUpdatePasswordLoading(false)
    }
  }

  const renderPictureButton = () => {
    if (base64) {
      if (updatePictureLoading) {
        return (
          <>
            <Button
              size="large"
              color="primary"
              variant={'contained'}
              component="span"
              onClick={() => updatePicture()}
            >
              {updatePictureLoading ? (
                <CircularProgress size={25} color="inherit" />
              ) : (
                t('buttons.savePicture')
              )}
            </Button>
          </>
        )
      } else {
        return (
          <>
            <Button
              size="large"
              color="primary"
              variant={'contained'}
              component="span"
              onClick={() => updatePicture()}
            >
              {updatePictureLoading ? (
                <CircularProgress size={25} color="inherit" />
              ) : (
                t('buttons.savePicture')
              )}
            </Button>
            <div style={{ width: 10 }} />
            <Button
              size="large"
              color="primary"
              component="span"
              onClick={() => setBase64('')}
            >
              {pictureLoading ? (
                <CircularProgress size={25} color="inherit" />
              ) : (
                t('buttons.cancel')
              )}
            </Button>
          </>
        )
      }
    } else {
      return (
        <>
          <label htmlFor="upload-file">
            <Button
              size="large"
              color="primary"
              variant={'outlined'}
              component="span"
            >
              {pictureLoading ? (
                <CircularProgress size={25} color="inherit" />
              ) : (
                t('buttons.changePicture')
              )}
            </Button>
          </label>
          <input
            accept="image/*"
            id="upload-file"
            type="file"
            style={{ display: 'none' }}
            onChange={setSelectedFile}
          />
        </>
      )
    }
  }

  return (
    <>
      <div className={classes.root}>
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">{t('commons.settings')}</Typography>
            <IconButton onClick={props.close} color="primary">
              <CloseIcon style={{ color: theme.palette.primary.main }} />
            </IconButton>
          </div>
          <div style={{ height: 10 }} />
          <Divider />
          <div style={{ height: 25 }} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Avatar
              src={base64 ? base64 : user?.url_avatar ? user?.url_avatar : ''}
              style={{
                width: 100,
                height: 100,
                boxShadow: `0 0 0 3px ${theme.palette.hover3}`,
              }}
            >
              {user?.name?.split(' ')[0].charAt(0).toUpperCase()}
              {user?.name?.split(' ')[1].charAt(0).toUpperCase()}
            </Avatar>
            <div style={{ width: 15 }} />
            {renderPictureButton()}
          </div>
          <div style={{ height: 30 }} />
          <Grid container direction="row" spacing={2}>
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
              <FormControl
                fullWidth
                style={{ marginTop: '5px' }}
                variant="outlined"
              >
                <InputLabel htmlFor="field-newPassword">
                  {t('fields.newPassword.label')}
                </InputLabel>
                <OutlinedInput
                  inputRef={newPassRef}
                  id="field-newPassword"
                  label={t('fields.newPassword.label')}
                  placeholder={t('fields.newPassword.placeHolder')}
                  type={isVisiblePassword ? 'text' : 'password'}
                  color="primary"
                  variant="outlined"
                  name="newPassword"
                  fullWidth={true}
                  error={formError?.newPassword ? true : false}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setIsVisiblePassword(!isVisiblePassword)}
                      >
                        {isVisiblePassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                <FormHelperText
                  style={{ marginBottom: '10px' }}
                  error={true}
                  id="standard-weight-helper-text-password"
                >
                  {formError?.newPassword &&
                    t(`fields.newPassword.errors.${formError.newPassword}`)}
                </FormHelperText>
              </FormControl>

              <FormControl
                fullWidth
                style={{ marginTop: '5px' }}
                variant="outlined"
              >
                <InputLabel htmlFor="field-passwordRepeat">
                  {t('fields.passwordRepeat.label')}
                </InputLabel>
                <OutlinedInput
                  inputRef={newPassRepeatRef}
                  id="field-passwordRepeat"
                  label={t('fields.passwordRepeat.label')}
                  placeholder={t('fields.passwordRepeat.placeHolder')}
                  type={isVisibleRepeatPassword ? 'text' : 'password'}
                  color="primary"
                  variant="outlined"
                  name="passwordRepeat"
                  fullWidth={true}
                  error={formError?.passwordRepeat ? true : false}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          setIsVisibleRepeatPassword(!isVisibleRepeatPassword)
                        }
                      >
                        {isVisibleRepeatPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                <FormHelperText
                  style={{ marginBottom: '10px' }}
                  error={true}
                  id="standard-weight-helper-text-repeat-password"
                >
                  {formError?.passwordRepeat &&
                    t(`fields.newPassword.errors.${formError.passwordRepeat}`)}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </div>
        <div>
          <Button
            size="large"
            color="primary"
            variant="contained"
            fullWidth={true}
            onClick={() => passwordCheck()}
            disabled={updatePasswordLoading}
          >
            {updatePasswordLoading ? (
              <CircularProgress size={25} color="inherit" />
            ) : (
              t('buttons.saveEditions')
            )}
          </Button>
        </div>
      </div>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbar}
        onClose={() => setSnackbar(false)}
        autoHideDuration={3000}
        message={snackbarMessage}
      />
    </>
  )
}

export default userSettings
